import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, deleteUser } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Users, 
  Building2, 
  Mail, 
  Phone, 
  Calendar,
  Shield,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Eye,
  Link as LinkIcon,
  ArrowRight,
  FileText,
  Trash2
} from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [partnerCount, setPartnerCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAndFetch();
  }, []);

  const checkAdminAndFetch = async () => {
    try {
      // Check if user is admin
      const adminCheck = await api.get("/admin/check");
      if (!adminCheck.data.is_admin) {
        toast.error("Admin access required");
        navigate("/");
        return;
      }
      setIsAdmin(true);

      // Fetch users and partners
      const [usersRes, partnersRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/partners")
      ]);
      setUsers(usersRes.data.users);
      setTotalUsers(usersRes.data.total_users);
      setPartnerCount(partnersRes.data.total_partners);
    } catch (error) {
      console.error("Error:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleUserDetails = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      return;
    }

    setExpandedUser(userId);

    if (!userDetails[userId]) {
      try {
        const response = await api.get(`/admin/users/${userId}`);
        setUserDetails(prev => ({
          ...prev,
          [userId]: response.data
        }));
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
  };

  const handleViewAsUser = async (userId, userName) => {
    try {
      const response = await api.post(`/admin/impersonate/${userId}`);
      toast.success(`Now viewing as ${userName}`);
      // Redirect to dashboard as that user
      navigate("/");
      // Force page reload to update auth state
      window.location.reload();
    } catch (error) {
      toast.error("Failed to view as user");
      console.error("Error impersonating user:", error);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteUser(userToDelete.user_id);
      toast.success(`User ${userToDelete.email} deleted successfully`);
      // Remove user from list
      setUsers(users.filter(u => u.user_id !== userToDelete.user_id));
      setTotalUsers(prev => prev - 1);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
      console.error("Error deleting user:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 lg:p-8">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate("/")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-8 h-8 text-[#547d91]" />
            Admin Dashboard
          </h2>
          <p className="text-slate-500 mt-1">
            Manage users and view account details
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#547d91]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#547d91]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalUsers}</p>
                <p className="text-sm text-slate-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {users.reduce((acc, u) => acc + (u.property_count || 0), 0)}
                </p>
                <p className="text-sm text-slate-500">Total Properties</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Link to="/admin/partners">
          <Card className="cursor-pointer hover:bg-slate-50 transition-colors h-full">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <LinkIcon className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{partnerCount}</p>
                    <p className="text-sm text-slate-500">Referral Partners</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Admin Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/admin/research">
          <Card className="cursor-pointer hover:bg-slate-50 transition-colors">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Research Documents</p>
                    <p className="text-sm text-slate-500">Upload & manage suburb PDFs</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Registered Users</CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No users registered yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Referred By</TableHead>
                    <TableHead>Properties</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <>
                      <TableRow 
                        key={user.user_id} 
                        className="cursor-pointer hover:bg-slate-50"
                        data-testid={`user-row-${user.user_id}`}
                      >
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>
                          {expandedUser === user.user_id ? (
                            <ChevronUp className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium" onClick={() => toggleUserDetails(user.user_id)}>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#547d91] flex items-center justify-center text-white text-sm font-medium">
                              {user.name?.charAt(0) || "?"}
                            </div>
                            {user.name}
                          </div>
                        </TableCell>
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>{user.email}</TableCell>
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>
                          {user.mobile ? (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3 text-slate-400" />
                              {user.mobile}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>
                          {user.referral_partner_name ? (
                            <Badge variant="outline" className="text-purple-600 border-purple-200">
                              {user.referral_partner_name}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">Direct</span>
                          )}
                        </TableCell>
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {user.property_count || 0}
                          </span>
                        </TableCell>
                        <TableCell onClick={() => toggleUserDetails(user.user_id)}>
                          <span className="flex items-center gap-1 text-slate-500">
                            <Calendar className="w-3 h-3" />
                            {formatDate(user.created_at)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewAsUser(user.user_id, user.name);
                              }}
                              className="text-[#547d91] hover:text-[#456a7a]"
                              data-testid={`view-as-${user.user_id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(user);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedUser === user.user_id && userDetails[user.user_id] && (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-slate-50 p-4">
                            <div className="space-y-3">
                              <h4 className="font-semibold text-slate-700">User Properties</h4>
                              {userDetails[user.user_id].properties.length === 0 ? (
                                <p className="text-slate-500 text-sm">No properties added yet</p>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {userDetails[user.user_id].properties.map((prop) => (
                                    <div key={prop.id} className="bg-white p-3 rounded-lg border border-slate-200">
                                      <p className="font-medium text-slate-900">{prop.address}</p>
                                      <p className="text-sm text-slate-500">{prop.suburb}, {prop.state}</p>
                                      <div className="flex justify-between mt-2 text-sm">
                                        <span className="text-[#547d91] font-medium">
                                          ${prop.current_value?.toLocaleString()}
                                        </span>
                                        <span className="text-slate-500">
                                          ${prop.weekly_rent || prop.rental_income_weekly}/wk
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
              <br /><br />
              This will permanently delete:
              <ul className="list-disc list-inside mt-2 text-red-600">
                <li>User account</li>
                <li>All {userToDelete?.property_count || 0} properties</li>
                <li>All associated expenses</li>
                <li>All session data</li>
              </ul>
              <br />
              <strong className="text-red-600">This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
